const activeClaims = new WeakMap();
const activeCompletions = new WeakMap();
const attachedRoots = new WeakMap();

/** Shared storefront presentation, installed before the native form can render. */
export function createOmnisendBrandStyle(formId, logoUrl, doc = document) {
  if (!/^[a-zA-Z0-9_-]+$/.test(formId)) return null;
  const p = 'omnisend-form-' + formId;
  // Native styles arrive after openForm; repeat this scope so their defaults cannot win ties.
  const root = '.' + p + '-container.' + p + '-container';
  const states = ['step-0-container','success-sections-container','subscribed-sections-container','unavailable-page-sections-container'];
  const active = states.map(state => `${root} .${p}-${state}`).join(',');
  const sections = states.map(state => `${root} .${p}-${state} > [class^="${p}-section-"]`).join(',');
  const heading = `${root} [class*="${p}-column-"] > [class*="${p}-text-"]:first-of-type`;
  let logo = 'none';
  try { const url = new URL(logoUrl, doc.baseURI); if (/^https?:$/.test(url.protocol)) logo = `url(${JSON.stringify(url.href)})`; } catch {}
  const style = doc.createElement('style');
  style.dataset.realmRewardBrand = formId;
  style.textContent = `
    ${root}{background:#030303!important;color:#fff!important;overflow-x:hidden!important;font-family:RealmRewardMono,monospace!important}
    ${root},${root} *{box-sizing:border-box}
    ${root}::before{content:'UNDERREALM';position:fixed;inset:0 0 auto;z-index:4;height:82px;padding:32px 40px;background:#030303;color:#fff;font:400 14px/18px RealmRewardMono,monospace;pointer-events:none}
    ${root}::after{content:'';position:fixed;z-index:5;left:50%;top:24px;width:42px;height:38px;transform:translateX(-50%);background-image:${logo};background-size:contain;background-repeat:no-repeat;background-position:center;filter:invert(1);pointer-events:none}
    ${root} .${p}-content{height:auto!important;min-height:100dvh;background:#030303!important}
    ${root} .${p}-content-inner{min-height:100dvh;padding:106px 32px 40px!important;background:#030303!important;border:0!important;overflow:visible!important}
    ${active}{width:100%!important;min-width:0!important;padding:0!important;background:#030303!important;align-items:center!important;justify-content:center!important}
    ${sections}{width:100%!important;max-width:520px!important;min-width:0!important;padding:24px 0!important;flex-grow:0!important;background:#030303!important}
    ${root} [class$="-opacity-layer"]{background:#030303!important}
    ${root} [class*="${p}-text-"],${root} p,${root} label,${root} li,${root} input,${root} a,${root} button{font-family:RealmRewardMono,monospace!important;color:#fff!important;letter-spacing:0!important}
    ${root} [class*="${p}-text-"] p{font-size:14px!important;line-height:1.65!important}
    ${heading},${heading} p,${heading} strong{font-family:RealmRewardDisplay,Arial,sans-serif!important;font-size:34px!important;font-weight:900!important;line-height:1.12!important;letter-spacing:-.25px!important;color:#fff!important}
    ${root} [class*="${p}-text-"] + [class*="${p}-text-"]{margin-top:20px!important}
    ${root} input[type="email"]{width:100%!important;min-height:58px!important;border:1px solid #777!important;border-radius:0!important;background:#030303!important;color:#fff!important;font-size:16px!important;padding:16px!important;box-shadow:none!important}
    ${root} input::placeholder{color:#aaa!important;opacity:1!important}
    ${root} input[type="checkbox"]{accent-color:#fff;color:#030303!important}
    ${root} [class*="${p}-action-"] button,${root} button[id^="${p}-action-"],${root} a[id^="${p}-action-"]{min-height:58px!important;padding:18px 22px!important;border:1px solid #fff!important;border-radius:0!important;background:#fff!important;color:#030303!important;font-size:13px!important;line-height:1.4!important}
    ${root} button[id^="${p}-action-"]:hover,${root} a[id^="${p}-action-"]:hover{background:#ddd!important;border-color:#ddd!important;color:#030303!important}
    ${root} input:focus-visible,${root} button:focus-visible,${root} a:focus-visible{outline:2px solid #fff!important;outline-offset:4px!important;box-shadow:none!important}
    ${root} .${p}-close-button{position:fixed!important;z-index:6!important;top:18px!important;right:24px!important}
    ${root} #${p}-close-action{width:48px!important;height:48px!important;padding:8px!important;background:transparent!important;border:0!important}
    ${root} .${p}-close-action-wrapper{background:transparent!important}
    ${root} #${p}-form-close-icon{fill:#fff!important;color:#fff!important;stroke:none!important;width:20px!important;height:20px!important}
    @media(max-width:760px){
      ${root}::before{height:66px;padding:24px 20px;font-size:11px;line-height:18px}
      ${root}::after{top:18px;width:34px;height:30px}
      ${root} .${p}-content-inner{padding:88px 24px 28px!important}
      ${root} .${p}-close-button{top:9px!important;right:10px!important}
      ${heading},${heading} p,${heading} strong{font-size:clamp(22px,5.5vw,30px)!important}
      ${root} [class*="${p}-text-"] p{font-size:13px!important}
    }
  `;
  doc.head.append(style);
  return style;
}

function rendered(element) {
  if (!element || element.isConnected === false || !element.getClientRects().length) return false;
  for (let current = element; current; current = current.parentElement) {
    const style = getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
  }
  return true;
}

/** Time truthful terminal screens after a matching submit; validation errors stay open. */
export function watchOmnisendSuccess(formId, {
  document: doc = document,
  eventTarget = window,
  signal,
  now = () => performance.now(),
  isVisible = rendered,
  holdMs = 4000,
  onCleanup = () => {}
} = {}) {
  if (!/^[a-zA-Z0-9_-]+$/.test(formId)) return () => {};
  const prefix = 'omnisend-form-' + formId;
  const selector = '.' + prefix + '-container';
  let stopped = false, nativeRoot = null, seenSuccess = false, terminalState = null;
  let last = now(), held = 0, waiting = 0, wasCounting = false, wasDocumentVisible = !doc.hidden;
  const style = doc.createElement('style');
  style.dataset.realmRewardSuccess = formId;
  const scoped = selector + '.realm-reward-native-success';
  style.textContent = `
    ${scoped},${scoped} .${prefix}-content,${scoped} .${prefix}-content-inner,
    ${scoped} .${prefix}-success-sections-container,${scoped} .${prefix}-close-action-wrapper{
      background:#030303!important;background-color:#030303!important;color:#fff!important;
    }
    ${scoped}{overflow-x:hidden!important}
    ${scoped} .${prefix}-success-sections-container{box-sizing:border-box!important;min-width:0!important}
    ${scoped} .${prefix}-success-sections-container > [class^="${prefix}-section-"]{
      box-sizing:border-box!important;min-width:0!important;width:100%!important;max-width:520px!important;
    }
    ${scoped} #${prefix}-close-action{color:#fff!important;background:transparent!important}
    ${scoped} #${prefix}-form-close-icon{fill:#fff!important;color:#fff!important;stroke:none!important}
  `;
  doc.head.append(style);
  let interval;
  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    clearInterval(interval);
    eventTarget.removeEventListener('omnisendForms', onForm);
    eventTarget.removeEventListener('pagehide', cleanup);
    doc.removeEventListener('visibilitychange', tick);
    signal?.removeEventListener('abort', cleanup);
    nativeRoot?.classList.remove('realm-reward-native-success');
    style.remove();
    onCleanup();
  };
  const onForm = event => {
    const detail = event.detail || {};
    const type = detail.eventName || detail.type;
    const id = String(detail.form?.id || '');
    if ((id === formId && type === 'close') || (id && id !== formId && type === 'view')) cleanup();
  };
  function tick() {
    if (stopped) return;
    const currentTime = now(), delta = Math.max(0, currentTime - last);
    last = currentTime;
    if (wasCounting) held += delta;
    if (wasDocumentVisible && !seenSuccess) waiting += delta;
    wasDocumentVisible = !doc.hidden;
    const currentRoot = doc.querySelector(selector);
    // Replacing the form must not close a new instance on an old submit timer.
    if (nativeRoot && currentRoot !== nativeRoot) { cleanup(); return; }
    if (currentRoot) nativeRoot = currentRoot;
    if (!nativeRoot) { if (waiting >= 60000) cleanup(); return; }
    const success = nativeRoot.querySelector('.' + prefix + '-success-sections-container');
    const subscribed = nativeRoot.querySelector('.' + prefix + '-subscribed-sections-container');
    const nextState = isVisible(subscribed) ? 'subscribed' : isVisible(success) ? 'success' : null;
    const showingSuccess = isVisible(nativeRoot) && !!nextState;
    if (seenSuccess && !showingSuccess) { cleanup(); return; }
    if (terminalState && nextState !== terminalState) held = 0;
    terminalState = nextState;
    wasCounting = showingSuccess && !doc.hidden;
    if (!showingSuccess) { if (waiting >= 60000) cleanup(); return; }
    seenSuccess = true;
    if (!nativeRoot.classList.contains('realm-reward-native-success')) nativeRoot.classList.add('realm-reward-native-success');
    if (doc.hidden || held < holdMs) return;
    const close = nativeRoot.querySelector('#' + prefix + '-close-action');
    if (!close || close.disabled || !isVisible(close)) return;
    // Use the form's existing native close action; never navigate or remove SDK DOM.
    try { close.click(); } catch { /* Keep the existing native close control usable if its SDK fails. */ } finally { cleanup(); }
  }
  eventTarget.addEventListener('omnisendForms', onForm);
  eventTarget.addEventListener('pagehide', cleanup, {once: true});
  doc.addEventListener('visibilitychange', tick);
  signal?.addEventListener('abort', cleanup, {once: true});
  if (signal?.aborted) { cleanup(); return cleanup; }
  interval = setInterval(tick, 100);
  tick();
  return cleanup;
}

export function attachOmnisendRewardAdapter(root = document) {
  if (attachedRoots.has(root)) return attachedRoots.get(root);
  const ownedClaims = new Set();
  const ownedCompletions = new Set();
  const ownedStyles = new Map();
  const onReward = event => {
    if (event.defaultPrevented) return;
    const host = event.target;
    if (!(host instanceof HTMLElement) || host.localName !== 'realm-reward-drop') return;
    const claim = event.detail;
    if (![5, 10, 15].includes(claim?.discount)) return;
    const formId = String(host.dataset['form' + claim.discount] || '').trim();
    if (!/^[a-zA-Z0-9_-]+$/.test(formId) || typeof claim?.complete !== 'function' || typeof claim?.fail !== 'function') return;
    event.preventDefault();
    if (!ownedStyles.has(formId)) ownedStyles.set(formId, createOmnisendBrandStyle(formId, host.dataset.logoUrl));
    activeClaims.get(host)?.();
    activeCompletions.get(host)?.();

    let finished = false;
    let timeout;
    const cleanup = () => {
      window.removeEventListener('omnisendForms', onForm);
      window.removeEventListener('pagehide', cancel);
      claim.signal?.removeEventListener('abort', cancel);
      clearTimeout(timeout);
      if (activeClaims.get(host) === cancel) activeClaims.delete(host);
      ownedClaims.delete(cancel);
    };
    const finish = success => {
      if (finished) return;
      finished = true;
      cleanup();
      if (success) {
        let stopWatcher;
        const stop = () => {
          stopWatcher?.();
          if (activeCompletions.get(host) === stop) activeCompletions.delete(host);
          ownedCompletions.delete(stop);
        };
        activeCompletions.set(host, stop);
        ownedCompletions.add(stop);
        stopWatcher = watchOmnisendSuccess(formId, {signal: claim.lifecycleSignal, onCleanup: stop});
        claim.complete({externalSuccess: true});
      }
      else claim.fail();
    };
    const cancel = () => finish(false);
    const onForm = omniEvent => {
      const detail = omniEvent.detail || {};
      if (String(detail.form?.id || '') !== formId) return;
      const type = detail.eventName || detail.type;
      if (type === 'view') {
        clearTimeout(timeout);
        claim.formOpened?.();
      }
      if (type === 'submit') finish(true);
      else if (type === 'close') finish(false);
    };
    window.addEventListener('omnisendForms', onForm);
    window.addEventListener('pagehide', cancel, {once: true});
    claim.signal?.addEventListener('abort', cancel, {once: true});
    activeClaims.set(host, cancel);
    ownedClaims.add(cancel);
    if (claim.signal?.aborted) { finish(false); return; }
    timeout = setTimeout(() => finish(false), 12000);
    try {
      window.omnisend = window.omnisend || [];
      window.omnisend.push(['openForm', formId]);
    } catch {
      finish(false);
    }
  };
  root.addEventListener('realm:reward', onReward);
  const detach = () => {
    root.removeEventListener('realm:reward', onReward);
    for (const cancel of [...ownedClaims]) cancel();
    for (const stop of [...ownedCompletions]) stop();
    for (const style of ownedStyles.values()) style?.remove();
    attachedRoots.delete(root);
  };
  attachedRoots.set(root, detach);
  return detach;
}

if (typeof document !== 'undefined') attachOmnisendRewardAdapter();
